import {
  Archive16Regular,
  Dismiss16Regular,
  Drafts16Regular,
  Flag16Regular,
  MailUnread16Regular
} from "@fluentui/react-icons";
import { JSX } from "react";
import { useLocation } from "react-router";

import { Button } from "components/button/Button";
import { InboxFiltersEnum, TranslatedCategories } from "models/inbox/inbox";
import { useRootStore } from "store";
import { useGlobalContext } from "app/store/GlobalContext";
import { Tooltip } from "components/tooltip/Tooltip";

import "./ActiveFilters.scss";

type FilterItem = {
  key: string;
  text?: string;
  content: JSX.Element;
  onClick: () => void;
};

export default function ActiveFilters() {
  const { handleNavigation } = useGlobalContext();
  const location = useLocation();

  const {
    setActiveFilters,
    activeFilters,
    resetActiveFilters,
    sentMessagesSearchTerm,
    fetchMyThreads,
    fetchAllThreads
  } = useRootStore((state) => state.inbox);
  const { workingCustomerId } = useRootStore((state) => state.global);

  const fetchFilteredThreads = () => {
    const redirectURL =
      location.pathname === "/inbox/my/new" ? location.pathname : "/inbox/my";
    handleNavigation({
      url: redirectURL,
      eventTitle: "Inbox_my"
    });

    fetchMyThreads(workingCustomerId, 0, sentMessagesSearchTerm);
    fetchAllThreads(workingCustomerId, 0, sentMessagesSearchTerm);
  };

  const renderActiveFilterButton = (item: FilterItem) => {
    const activeFilterBtn = (
      <Button
        size="xs"
        variant="outline"
        color="neutral"
        onClick={() => {}}
        key={item.key}
        className="mr-2 mb-2"
      >
        <div className="d-flex align-items-center">
          {item.content}
          <Dismiss16Regular
            onClick={() => {
              item.onClick();
              fetchFilteredThreads();
            }}
            className="ml-2"
          />
        </div>
      </Button>
    );

    if (item.text) {
      return (
        <Tooltip content={<span>{item.text}</span>}>{activeFilterBtn}</Tooltip>
      );
    } else {
      return activeFilterBtn;
    }
  };

  const resetFilters = () => {
    resetActiveFilters();
    fetchFilteredThreads();
  };

  const filterItems: FilterItem[] = [
    {
      key: InboxFiltersEnum.FLAGGED,
      text: "Flagged",
      content: <Flag16Regular />,
      onClick: () => {
        setActiveFilters({
          ...activeFilters,
          flagged: false
        });
      }
    },
    {
      key: InboxFiltersEnum.UNREAD,
      text: "Unread",
      content: <MailUnread16Regular />,
      onClick: () => {
        setActiveFilters({
          ...activeFilters,
          unread: false
        });
      }
    },
    {
      key: InboxFiltersEnum.DRAFTS,
      text: "Drafts",
      content: <Drafts16Regular />,
      onClick: () => {
        setActiveFilters({
          ...activeFilters,
          drafts: false
        });
      }
    },
    {
      key: InboxFiltersEnum.ARCHIVED,
      text: "Arkiverade",
      content: <Archive16Regular />,
      onClick: () => {
        setActiveFilters({
          ...activeFilters,
          archived: false
        });
      }
    }
  ];

  const activeFilterItems: FilterItem[] = [
    ...filterItems.filter((item) => activeFilters[item.key] === true),

    ...(activeFilters.categories || []).map((category) => ({
      key: category,
      content: <span>{TranslatedCategories[category]}</span>,
      onClick: () => {
        setActiveFilters({
          ...activeFilters,
          categories: activeFilters.categories.filter(
            (activeCategory) => activeCategory !== category
          )
        });
      }
    }))
  ];

  const hasActiveFilters = activeFilterItems.length > 0;

  return (
    <div
      className={`d-flex align-items-center ${hasActiveFilters ? "mb-4" : ""}`}
    >
      <div className="active-filters-wrapper d-flex">
        {activeFilterItems.map((item) => renderActiveFilterButton(item))}
      </div>
      {hasActiveFilters && (
        <div className="active-filters-clear-all">
          <Button
            onClick={resetFilters}
            size="sm"
            color="neutral"
            className="mb-2"
          >
            <div className="d-flex align-items-center">
              <span>Rensa alla</span>
              <Dismiss16Regular className="ml-2" />
            </div>
          </Button>
        </div>
      )}
    </div>
  );
}
